import { useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { AlertTriangle, Copy, RefreshCw, Bug, Trash2 } from "lucide-react";

interface ErrorFallbackProps {
  error: Error;
  resetError: () => void;
}

export function ErrorFallback({ error, resetError }: ErrorFallbackProps) {
  const [eventId, setEventId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const sentryEventId = Sentry.captureException(error);
    setEventId(sentryEventId);
  }, [error]);

  const copyErrorId = async () => {
    if (eventId) {
      await navigator.clipboard.writeText(eventId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const clearStorage = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = '/';
  };

  return (
    <div
      data-testid="app-error"
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-50 p-4"
    >
      <div className="max-w-md w-full bg-white rounded-lg shadow-xl border border-red-100 p-8">
        <div className="flex items-center justify-center w-16 h-16 mx-auto mb-6 bg-red-100 rounded-full">
          <AlertTriangle className="w-8 h-8 text-red-600" />
        </div>

        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Oops! Something went wrong
          </h1>
          <p className="text-gray-600">
            We've encountered an unexpected error. Our team has been notified and is working on a fix.
          </p>
        </div>

        {eventId && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Bug className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Error ID</span>
              </div>
              <button
                onClick={copyErrorId}
                className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-200 hover:bg-gray-300 rounded transition-colors"
                data-testid="error-copy-button"
              >
                <Copy className="w-3 h-3" />
                {copied ? "Copied!" : "Copy"}
              </button>
            </div>
            <code className="text-xs text-gray-800  break-all">
              {eventId}
            </code>
            <p className="text-xs text-gray-500 mt-2">
              Share this ID with support for faster debugging
            </p>
          </div>
        )}

        <div className="space-y-3">
          <button
            onClick={resetError}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
            data-testid="error-retry-button"
          >
            <RefreshCw className="w-4 h-4" />
            Try again
          </button>

          <button
            onClick={clearStorage}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-lg transition-colors"
            data-testid="error-clear-storage-button"
          >
            <Trash2 className="w-4 h-4" />
            Clear Storage & Reload
          </button>
        </div>

        <div className="mt-6 pt-6 border-t border-gray-200 text-center">
          <p className="text-xs text-gray-500">
            If this problem persists, please contact our support team
          </p>
        </div>
      </div>
    </div>
  );
}
